import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUpIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export interface CashbackBadgeProps {
  /** Cashback percentage as decimal (e.g. 0.02 for 2%) */
  percentage: number;
  /** Custom label text. If omitted, uses default "X% cashback" translation. */
  label?: string;
  /** Days left for the cashback offer */
  daysLeft?: number;
  /** Show a blinking live indicator dot (e.g. in deposit input). Defaults to false. */
  isLive?: boolean;
  /** Color variant. Defaults to 'green'. */
  variant?: 'green' | 'blue';
  /** Optional className */
  className?: string;
}

/**
 * CashbackBadge — Reusable chip for displaying cashback incentives.
 *
 * Used in:
 * - Invoice rows (CheckoutInvoiceList, InvoiceTable)
 * - Advance deposit input (with `isLive` blinking indicator)
 * - CashbackBanner (inline badge)
 *
 * @example
 * ```tsx
 * <CashbackBadge percentage={0.02} daysLeft={3} />
 * // Renders: [icon] 2% cashback · 3 days left
 *
 * <CashbackBadge percentage={0.03} label="3% cashback" isLive />
 * // Renders: [blinking dot] 3% cashback
 *
 * <CashbackBadge percentage={0.05} variant="blue" />
 * // Renders: [icon] 5% cashback (blue)
 * ```
 */
export const CashbackBadge: React.FC<CashbackBadgeProps> = ({
  percentage,
  label,
  daysLeft,
  isLive = false,
  variant = 'green',
  className,
}) => {
  const { t } = useTranslation('m-payment');

  const displayPercentage = Math.round(percentage * 100);

  const badgeLabel = label ?? t('cashback.cashback_percentage', { percentage: displayPercentage });

  const urgencyText =
    daysLeft !== undefined && daysLeft >= 0
      ? daysLeft === 0
        ? t('cashback.expires_today')
        : daysLeft === 1
        ? t('cashback.expires_tomorrow')
        : t('cashback.days_left', { count: daysLeft })
      : undefined;

  const variantStyles = {
    green: {
      base: 'bg-teal-50 text-teal-700 border border-teal-200',
      live: 'bg-teal-100 border-teal-300 shadow-teal-200/50',
      dot: 'bg-teal-400',
      dotCenter: 'bg-teal-500',
      icon: 'text-teal-500',
      separator: 'text-teal-300',
      urgency: 'text-teal-600',
    },
    blue: {
      base: 'bg-blue-50 text-blue-700 border border-blue-200',
      live: 'bg-blue-100 border-blue-300 shadow-blue-200/50',
      dot: 'bg-blue-400',
      dotCenter: 'bg-blue-500',
      icon: 'text-blue-500',
      separator: 'text-blue-300',
      urgency: 'text-blue-600',
    },
  };

  const v = variantStyles[variant];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
        v.base,
        isLive && 'font-bold shadow-sm',
        isLive && v.live,
        className,
      )}>
      {/* Live blinking dot OR static icon */}
      {isLive ? (
        <span className="relative flex size-2.5">
          <span
            className={cn(
              'absolute inline-flex size-full rounded-full animate-ping [animation-duration:1s]',
              v.dot,
            )}
          />
          <span className={cn('relative inline-flex size-2.5 rounded-full', v.dotCenter)} />
        </span>
      ) : (
        <TrendingUpIcon className={cn('size-3', v.icon)} />
      )}

      <span className="font-semibold">{badgeLabel}</span>

      {urgencyText && (
        <>
          <span className={v.separator}>·</span>
          <span className={cn('font-medium', v.urgency)}>{urgencyText}</span>
        </>
      )}
    </span>
  );
};
